import {
  AccountManagementActionTypesResponseObject,
  CoordinatorProfileResponse,
} from 'web-library/models/AccountManagementConfiguration';
import React, { useCallback, useMemo } from 'react';
import { filterAccountManagementActionTypes, prepareFiles } from 'web-library';
import { UseFormReturn } from 'react-hook-form';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { MutationStatus, UseMutateFunction } from 'react-query';
import {
  RequestFileRequest,
  RequestFileResponse,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import {
  CoordinatorProfileResponseIsDisableInputs,
  CreateEditCoordinatorSchemaData,
} from './CreateEditCoordinatorSchema';
import { useGetSchoolsByType } from '../../../api-refactored/non-national-exam-registration/useGetSchoolsByType';
import { IExamMasterCodeItem } from '../../../api/user/model';
import { AccountTypeMode } from '../UserAccountManagementListing';
import { useGetAccountManagementActionTypes } from '../../../api-refactored/account-management/useGetAccountManagementActionTypes';
import RouteKey from '../../../routes/key';

const { admin } = RouteKey.child;
const { userAccountManagement } = RouteKey.admin.child;

interface useCreateEditViewCoordinatorAccountDetailsProps {
  formHook: UseFormReturn<any>;
  coordinatorProfileDetails: CoordinatorProfileResponse;
  setDeleteAccountModalMessage: React.Dispatch<React.SetStateAction<string>>;
  isApproverNeededAction: boolean;
  isEditState: boolean;
  setIsEditState: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenConfirmChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  setUploadFileErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  formDefaultValues: any;
  uploadFileErrorMessage: string;
  postRequestFilePresigned: UseMutateFunction<
    RequestFileResponse[],
    AxiosError<any>,
    RequestFileRequest,
    unknown
  >;
  uuid: string;
  setFilteredActionTypes: React.Dispatch<
    React.SetStateAction<AccountManagementActionTypesResponseObject[]>
  >;
  isCreateState: boolean;
}

export const useCreateEditViewCoordinatorAccountDetails = ({
  formHook,
  coordinatorProfileDetails,
  setDeleteAccountModalMessage,
  isApproverNeededAction,
  isEditState,
  setIsEditState,
  setOpenConfirmChangesModal,
  setShouldSubmit,
  files,
  setFiles,
  setApiButtonStatus,
  setUploadFileErrorMessage,
  formDefaultValues,
  uploadFileErrorMessage,
  postRequestFilePresigned,
  uuid,
  setFilteredActionTypes,
  isCreateState,
}: useCreateEditViewCoordinatorAccountDetailsProps) => {
  const history = useHistory();
  const { getValues, reset } = formHook;

  const { refetch: refetchAccountManagementActionTypes } = useGetAccountManagementActionTypes({
    onSuccess: (res) =>
      filterAccountManagementActionTypes(
        res,
        coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
          .activeStatus as string,
        coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
          .blockStatus as string,
        coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
          .lockStatus as string,
        coordinatorProfileDetails?.coordinatorDetail.profileUpdateApprovalModel
          .accountType as string,
        setFilteredActionTypes
      ),
  });
  const handleConfirmChanges = useCallback(() => {
    if (uploadFileErrorMessage.length === 0) {
      const { filenames } = prepareFiles(files, setFiles);
      setShouldSubmit(true);
      setApiButtonStatus('loading');
      setOpenConfirmChangesModal(false);

      if (filenames.length) {
        postRequestFilePresigned({
          filenames,
          uuid,
        });
      }
    }
  }, [
    files,
    postRequestFilePresigned,
    setApiButtonStatus,
    setFiles,
    setOpenConfirmChangesModal,
    setShouldSubmit,
    uploadFileErrorMessage.length,
    uuid,
  ]);

  const handleBackToCoordinatorListing = useCallback(
    () =>
      history.push(`/${admin}/${userAccountManagement.route}`, {
        selectedRequestTypeMode: AccountTypeMode.Coordinator,
      }),
    [history]
  );

  const handleBackToCoordinatorListingWithAlert = useCallback(
    () =>
      history.push(`/${admin}/${userAccountManagement.route}`, {
        selectedRequestTypeMode: AccountTypeMode.Coordinator,
        action: getValues('action')?.description,
        email: coordinatorProfileDetails?.coordinatorDetail.email,
      }),
    [coordinatorProfileDetails?.coordinatorDetail.email, getValues, history]
  );
  const handleEditParticulars = useCallback(() => {
    reset(formDefaultValues);
    setIsEditState(true);
    setFiles([]);
    setApiButtonStatus('idle');
    setUploadFileErrorMessage('');
  }, [
    formDefaultValues,
    reset,
    setApiButtonStatus,
    setFiles,
    setIsEditState,
    setUploadFileErrorMessage,
  ]);
  const handleConfirmChangesButton = useCallback(() => {
    if (
      getValues('action')?.code === 'DELETE' &&
      !!coordinatorProfileDetails?.coordinatorDetail?.existingRegistrationsMessage
    ) {
      setDeleteAccountModalMessage(
        coordinatorProfileDetails?.coordinatorDetail?.existingRegistrationsMessage
      );
    } else if (isApproverNeededAction || isEditState) {
      setOpenConfirmChangesModal(true);
    } else {
      setShouldSubmit(true);
      setApiButtonStatus('loading');
    }
  }, [
    coordinatorProfileDetails?.coordinatorDetail?.existingRegistrationsMessage,
    getValues,
    isApproverNeededAction,
    isEditState,
    setApiButtonStatus,
    setDeleteAccountModalMessage,
    setOpenConfirmChangesModal,
    setShouldSubmit,
  ]);
  return {
    refetchAccountManagementActionTypes,
    handleConfirmChanges,
    handleBackToCoordinatorListing,
    handleBackToCoordinatorListingWithAlert,
    handleEditParticulars,
    handleConfirmChangesButton,
  };
};

interface useEditViewCoordinatorFormValuesProps {
  coordinatorProfileDetails: CoordinatorProfileResponse;
  isEditState: boolean;
  isApproverNeededAction: boolean;
  isCreateState: boolean;
}

interface useEditViewCoordinatorFormValuesPropsReturn {
  disabledFields: CoordinatorProfileResponseIsDisableInputs;
  formDefaultValues: CreateEditCoordinatorSchemaData;
}

export const useEditViewCoordinatorFormValues = ({
  coordinatorProfileDetails,
  isEditState,
  isApproverNeededAction,
  isCreateState,
}: useEditViewCoordinatorFormValuesProps): useEditViewCoordinatorFormValuesPropsReturn => {
  const { data: dataOrganisation } = useGetSchoolsByType(true, 'NMOE');

  const organisationDefaultObject = useMemo(
    () =>
      dataOrganisation &&
      dataOrganisation.find(
        (obj) => obj.name === coordinatorProfileDetails?.coordinatorDetail?.organisation.description
      ),
    [coordinatorProfileDetails?.coordinatorDetail?.organisation.description, dataOrganisation]
  );
  const formDefaultValues = useMemo(
    () =>
      ({
        salutation: coordinatorProfileDetails?.coordinatorDetail.salutation,
        coordinatorName: coordinatorProfileDetails?.coordinatorDetail.coordinatorName,
        privateCentreSchool: organisationDefaultObject,
        dateOfBirth: coordinatorProfileDetails?.coordinatorDetail.dateOfBirth,
        designation: coordinatorProfileDetails?.coordinatorDetail.designation,
        email: coordinatorProfileDetails?.coordinatorDetail.email,
        mobileContactNumber: coordinatorProfileDetails?.coordinatorDetail.contactNumber.number,
        mobileCountryCode: coordinatorProfileDetails?.coordinatorDetail.contactNumber
          .countryCode as unknown as IExamMasterCodeItem,
        remarks: '',
        expiryDate: coordinatorProfileDetails?.coordinatorDetail.expiryDate,
      } as CreateEditCoordinatorSchemaData),
    [
      coordinatorProfileDetails?.coordinatorDetail.contactNumber.countryCode,
      coordinatorProfileDetails?.coordinatorDetail.contactNumber.number,
      coordinatorProfileDetails?.coordinatorDetail.coordinatorName,
      coordinatorProfileDetails?.coordinatorDetail.dateOfBirth,
      coordinatorProfileDetails?.coordinatorDetail.designation,
      coordinatorProfileDetails?.coordinatorDetail.email,
      coordinatorProfileDetails?.coordinatorDetail.salutation,
      organisationDefaultObject,
      coordinatorProfileDetails?.coordinatorDetail.expiryDate,
    ]
  );
  const disabledFields = useMemo(
    () => ({
      action: isEditState,
      approver: !isApproverNeededAction && !isEditState,
      remarks: !isApproverNeededAction && !isEditState,
      salutation: !isEditState,
      coordinatorName: !isEditState,
      privateCentreSchool: !isEditState,
      dateOfBirth: !isEditState,
      designation: !isEditState,
      email: !isEditState,
      mobileContactNumber: !isEditState,
      mobileCountryCode: !isEditState,
      expiryDate: !isCreateState,
    }),
    [isApproverNeededAction, isEditState, isCreateState]
  );

  return {
    formDefaultValues,
    disabledFields,
  };
};
